import { forwardRef, isValidElement, ReactNode } from "react";
import { useDisclosure, useHover, useMergedRef } from "@mantine/hooks";
import { rem } from "@mantine/core";
import { faEllipsis, IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { Pill } from "../../../pill";
import { Popover } from "../../../popover";
import { Menu } from "../../../menu";
import { ActionIcon } from "../../../action-icon";
import { Icon } from "../../../icon";
import { useInteractionModalContext } from "../hooks";
import { kebabCase } from "lodash";

/**
 * A forwardRef pill component displaying a message tag with optional edit/remove actions and tooltip popover.
 * @param {IconDefinition} props.icon - The icon displayed on the tag pill.
 * @param {string} props.text - The text label of the tag.
 * @param {ReactNode} props.editTagButton - Optional edit action rendered in the tag's dropdown menu.
 * @param {ReactNode} props.removeTagButton - Optional remove action rendered in the tag's dropdown menu.
 * @param {ReactNode} props.tagTooltipContent - Optional content displayed in the tag's popover tooltip.
 * @returns {JSX.Element} The Tag component.
 */
const Tag = forwardRef<
    HTMLDivElement,
    {
        icon: IconDefinition;
        text: string;
        editTagButton?: ReactNode;
        removeTagButton?: ReactNode;
        tagTooltipContent?: ReactNode;
    }
>(({ icon, text, editTagButton, removeTagButton, tagTooltipContent }, ref) => {
    const { elements } = useInteractionModalContext();
    const { hovered, ref: hoverRef } = useHover();
    const [opened, { close, open }] = useDisclosure(false);
    const [menuOpened, { close: closeMenu, toggle: toggleMenu }] = useDisclosure(false, {});
    const mergedRef = useMergedRef(ref, hoverRef);

    return (
        <Popover
            position="bottom"
            disabled={typeof elements?.tags?.tooltipContent === "undefined"}
            withArrow
            shadow="md"
            opened={opened && !menuOpened}
        >
            <Popover.Target>
                <Pill
                    variant="subtle"
                    rightSection={
                        isValidElement(editTagButton) || isValidElement(removeTagButton) ? (
                            <Menu
                                keepMounted
                                opened={menuOpened}
                                closeOnClickOutside
                                closeOnItemClick
                                onClose={() => {
                                    closeMenu();
                                    close();
                                }}
                                onOpen={close}
                            >
                                <Menu.Target>
                                    <ActionIcon
                                        onClick={toggleMenu}
                                        size="xs"
                                        variant="subtle"
                                        c="var(--mantine-color-text)"
                                        icon={hovered ? faEllipsis : icon}
                                    />
                                </Menu.Target>
                                <Menu.Dropdown>
                                    {editTagButton}
                                    {removeTagButton}
                                </Menu.Dropdown>
                            </Menu>
                        ) : (
                            <Icon icon={icon} size="xs" />
                        )
                    }
                    ref={mergedRef}
                    data-testid={`chat-message-tag-${kebabCase(text)}`}
                    onMouseEnter={open}
                    onMouseLeave={close}
                >
                    {text}
                </Pill>
            </Popover.Target>
            {elements?.tags?.tooltipContent && (
                <Popover.Dropdown miw={rem(380)} maw={rem(500)}>
                    {tagTooltipContent}
                </Popover.Dropdown>
            )}
        </Popover>
    );
});
Tag.displayName = "Tag";

export { Tag };
